const cities = [
  { name: "Texarkana, AR", zips: "71854, 71855" },
  { name: "Texarkana, TX", zips: "75501, 75503, 75504" },
  { name: "Wake Village", zips: "75501" },
  { name: "New Boston, TX", zips: "75570" },
  { name: "Hope, AR", zips: "71801" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home or office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function TexarkanaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Texarkana &amp; the Ark-La-Tex Region
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Texarkana occupies one of the most distinctive geographic positions in the American South — a city that literally straddles the Arkansas-Texas state line, with a federal building split down the middle and residents who commute across two states daily. That border-straddling identity is matched by a unique regional economy built on railroads, timber, healthcare, and logistics — all industries with meaningful international travel components.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Union Pacific and BNSF both maintain significant operations in Texarkana, and railroad industry professionals regularly attend international training programs, safety conferences, and supplier meetings in Canada, Mexico, and Europe. Logistics and supply chain managers at major distribution facilities in the Ark-La-Tex corridor face similar travel demands. Unlike metro areas with large passport agencies nearby, Texarkana travelers must drive to either Dallas-Fort Worth (170 miles) or Little Rock (140 miles) for a regional agency appointment — or discover that neither has immediate availability.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Texarkana College and Texas A&M University-Texarkana have a combined enrollment of over 8,000 students, and both institutions send students abroad for study programs, service-learning trips, and international internships. First-time applicants — which describes most college-age travelers — need guidance through the acceptance clerk process, and the compressed timelines of university program deadlines make expedited processing essential.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              The church and missionary community in the Ark-La-Tex region is one of the most active in the South. Hundreds of churches in Miller County, Bowie County, and surrounding areas send mission teams annually to Mexico, Guatemala, Honduras, East Africa, and Southeast Asia — often with departure windows of two to four weeks after team selection. Fast Passport Center provides the remote, professional expedited service that Texarkana travelers need without requiring a half-day drive to a distant passport agency.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Texarkana, AR</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving the Ark-La-Tex tri-state region</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-5 leading-relaxed">
                After signing up online, ship your documents overnight to us — no office visit required for most services. We review, submit, and handle every detail while you focus on your railroad schedule, mission preparation, or academic deadlines.
              </p>
              <a
                href="https://fastpassportcenter.com/checkout/"
                className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
              >
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the Ark-La-Tex region — start online, ship overnight, and we handle the rest. No need to drive to Dallas or Little Rock for a passport agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Little Rock", href: "/cities/little-rock" },
                    { name: "Fort Smith", href: "/cities/fort-smith" },
                    { name: "Hot Springs", href: "/cities/hot-springs" },
                    { name: "Pine Bluff", href: "/cities/pine-bluff" },
                    { name: "Benton", href: "/cities/benton" },
                    { name: "Conway", href: "/cities/conway" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Ark-La-Tex Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}